import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const BlogListPage = () => {
    const [allBlogs, setAllBlogs] = useState([]);
    const [filteredBlogs, setFilteredBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeFilter, setActiveFilter] = useState('All');

    const filters = ['All', 'Gold Rate', 'Education', 'Wedding', 'Care Guide'];

    useEffect(() => {
        const fetchBlogs = async () => {
            try {
                const response = await fetch('/blog-data.json');
                const data = await response.json();

                // Professional Static Scheduling Logic
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Compare dates only

                const published = data.filter(post => {
                    const pubDate = new Date(post.publishDate);
                    pubDate.setHours(0, 0, 0, 0); // Normalize to midnight
                    return pubDate <= today;
                });

                // Sort by newest first
                const sorted = published.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate));

                setAllBlogs(sorted);
                setFilteredBlogs(sorted);
                setLoading(false);
            } catch (error) {
                console.error("Failed to load blog data:", error);
                setLoading(false);
            }
        };

        fetchBlogs();

        // SEO Title
        document.title = "Jewellery Blog & Insights | MAB Jewels (Manoj Abhushan Bhandar, Rajasthan)";
    }, []);

    const handleFilterChange = (category) => {
        setActiveFilter(category);
        if (category === 'All') {
            setFilteredBlogs(allBlogs);
        } else {
            // Flexible matching: check if category matches or if it's in tags/title implicitly
            const filtered = allBlogs.filter(blog =>
                blog.category.includes(category) ||
                (category === 'Gold Rate' && blog.title.toLowerCase().includes('rate')) ||
                (category === 'Buying Guide' && blog.title.toLowerCase().includes('guide'))
            );
            setFilteredBlogs(filtered);
        }
    };

    if (loading) {
        return <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>;
    }

    return (
        <div className="page-container">
            {/* Hero Section */}
            <div className="blog-hero" style={{
                backgroundImage: 'url(/assets/blog/mab-journal-hero.jpg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                padding: '120px 20px',
                textAlign: 'center',
                color: '#FFFFFF', // Force White
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* Dark Overlay for Contrast - Increased Opacity */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.65)', // Increased from 0.5 to 0.65 for better text visibility
                    zIndex: 1
                }}></div>

                <div style={{ position: 'relative', zIndex: 2 }}>
                    <h1 style={{
                        fontFamily: 'Playfair Display, serif',
                        fontSize: '3.5rem',
                        marginBottom: '15px',
                        fontWeight: '700',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.8)', // Stronger shadow
                        letterSpacing: '1px',
                        color: '#FFFFFF' // Explicit White
                    }}>
                        The MAB Journal
                    </h1>
                    <p style={{
                        fontSize: '1.3rem',
                        maxWidth: '700px',
                        margin: '0 auto 30px',
                        opacity: '1', // Removed opacity
                        fontWeight: '400',
                        lineHeight: '1.6',
                        textShadow: '1px 1px 3px rgba(0,0,0,0.8)',
                        color: '#FFFFFF' // Explicit White
                    }}>
                        Expert insights on gold, jewellery care, and Rajputi traditions.
                    </p>

                    {/* CTA Button */}
                    <Link to="/live-gold-rate" style={{
                        display: 'inline-block',
                        padding: '12px 28px',
                        border: '2px solid rgba(255,255,255,1)', // Full opacity border
                        borderRadius: '30px',
                        color: '#FFFFFF',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        fontWeight: '600',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0,0,0,0.3)'
                    }}>
                        Latest Gold Rate in Rajasthan →
                    </Link>
                </div>
            </div>

            <div className="content-wrapper" style={{ maxWidth: '1200px', margin: '40px auto 80px', padding: '0 20px' }}>

                {/* Filters */}
                <div className="blog-filters" style={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                    marginBottom: '50px'
                }}>
                    {filters.map(filter => (
                        <button
                            key={filter}
                            onClick={() => handleFilterChange(filter)}
                            style={{
                                padding: '8px 20px',
                                borderRadius: '25px',
                                border: '1px solid #e0e0e0',
                                background: activeFilter === filter ? '#B8860B' : 'white',
                                color: activeFilter === filter ? 'white' : '#666',
                                fontSize: '0.95rem',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                fontFamily: 'Inter, sans-serif'
                            }}
                        >
                            {filter}
                        </button>
                    ))}
                </div>

                {filteredBlogs.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '100px 0', color: '#666' }}>
                        <h3>No articles found in this category.</h3>
                        <p>Check back soon for more insights!</p>
                    </div>
                ) : (
                    <div className="blog-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                        gap: '40px'
                    }}>
                        {filteredBlogs.map(blog => (
                            <Link to={`/blog/${blog.slug}`} key={blog.id} style={{ textDecoration: 'none' }}>
                                <div className="blog-card" style={{
                                    background: 'white',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    transition: 'transform 0.3s ease',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column'
                                }}>
                                    <div className="blog-img-placeholder" style={{
                                        height: '220px',
                                        backgroundColor: '#f0e6d6',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#B8860B',
                                        overflow: 'hidden'
                                    }}>
                                        {blog.image ? (
                                            <img
                                                src={blog.image}
                                                alt={blog.title}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease' }}
                                                className="hover-zoom"
                                            />
                                        ) : (
                                            <span style={{ fontSize: '3rem' }}>💎</span>
                                        )}
                                    </div>

                                    <div className="blog-content" style={{ padding: '25px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <div className="blog-meta" style={{
                                            fontSize: '0.8rem',
                                            color: '#B8860B',
                                            textTransform: 'uppercase',
                                            letterSpacing: '1px',
                                            fontWeight: '600',
                                            marginBottom: '8px'
                                        }}>
                                            {blog.category}
                                        </div>

                                        <h3 style={{
                                            fontFamily: 'Playfair Display',
                                            color: '#1a1a1a',
                                            fontSize: '1.4rem',
                                            marginBottom: '10px',
                                            lineHeight: '1.3'
                                        }}>
                                            {blog.title}
                                        </h3>

                                        {/* Trust Signal Line */}
                                        <div style={{
                                            fontSize: '0.75rem',
                                            color: '#888',
                                            marginBottom: '15px',
                                            fontStyle: 'italic'
                                        }}>
                                            By MAB Jewels Editorial Team · Trusted since 2002
                                        </div>

                                        <p style={{
                                            color: '#666',
                                            fontSize: '0.95rem',
                                            lineHeight: '1.6',
                                            marginBottom: '20px',
                                            flex: 1
                                        }}>
                                            {blog.metaDescription.substring(0, 100)}...
                                        </p>

                                        <span style={{
                                            color: '#B8860B',
                                            fontWeight: '600',
                                            fontSize: '0.9rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }}>
                                            Read Article →
                                        </span>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BlogListPage;
